import { Button, Separator } from "@monbits/ui";
import { Ban, CheckCircle, Clock, ExternalLink, XCircle } from "lucide-react";
import * as React from "react";
import { useOutletContext, useParams } from "react-router-dom";
import CopyButton from "src/components/copy-button/copy-button";
import DetailRow from "src/components/detail-row/detail-row";
import { EntityDrawer } from "src/components/entity-drawer";
import { StatusBadge } from "src/components/status-badge";
import { TypeBadge } from "src/components/type-badge";
import type { TransactionsOutletContext } from "./transactions-layout";

const TransactionDetail: React.FC = () => {
	const { transactionId } = useParams<{ transactionId: string }>();
	const outletContext = useOutletContext<
		TransactionsOutletContext | undefined
	>();
	const transactions = outletContext?.transactions ?? [];
	const isLoading = outletContext?.isLoading ?? false;

	const parsedId = React.useMemo(() => {
		if (!transactionId) {
			return null;
		}
		const id = Number.parseInt(transactionId, 10);
		return Number.isNaN(id) ? null : id;
	}, [transactionId]);

	const transaction = React.useMemo(() => {
		if (parsedId === null) {
			return null;
		}
		return transactions.find((item) => item.id === parsedId) ?? null;
	}, [transactions, parsedId]);

	const isNotFound =
		!isLoading &&
		(parsedId === null || (parsedId !== null && transaction === null));

	const formatDate = (dateStr: string): string => {
		const date = new Date(dateStr);
		if (Number.isNaN(date.getTime())) {
			return dateStr;
		}

		return date.toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const formatAmount = (value?: string | null): string => {
		if (!value) {
			return "0";
		}
		return value;
	};

	const getStatusConfig = (
		status: string,
	): {
		type: "success" | "warning" | "error" | "info" | "default";
		icon: typeof CheckCircle;
		label: string;
	} => {
		const normalized = status?.toLowerCase?.() ?? "";

		switch (normalized) {
			case "completed":
			case "completedinv":
				return { type: "success", icon: CheckCircle, label: "Completed" };
			case "success":
				return { type: "success", icon: CheckCircle, label: "Success" };
			case "paid":
				return { type: "success", icon: CheckCircle, label: "Paid" };
			case "pending":
				return { type: "warning", icon: Clock, label: "Pending" };
			case "processing":
				return { type: "info", icon: Clock, label: "Processing" };
			case "inprogress":
			case "inprogressinv":
				return { type: "info", icon: Clock, label: "In Progress" };
			case "confirmed":
				return { type: "info", icon: CheckCircle, label: "Confirmed" };
			case "on_hold":
			case "onhold":
				return { type: "warning", icon: Clock, label: "On Hold" };
			case "failed":
			case "error":
				return { type: "error", icon: XCircle, label: "Failed" };
			case "canceled":
			case "cancelled":
				return { type: "default", icon: Ban, label: "Cancelled" };
			case "expired":
				return { type: "default", icon: Ban, label: "Expired" };
			default:
				return { type: "default", icon: Clock, label: status };
		}
	};

	const getTypeBadgeType = (
		type: string,
	): "incoming" | "internal" | "withdrawal" | "fund_return" | "default" => {
		switch (type?.toLowerCase?.()) {
			case "incoming":
				return "incoming";
			case "internal":
				return "internal";
			case "withdrawal":
			case "pool_withdrawal":
				return "withdrawal";
			case "fund_return":
				return "fund_return";
			default:
				return "default";
		}
	};

	const formatTransactionType = (type: string): string => {
		switch (type?.toLowerCase?.()) {
			case "incoming":
				return "Incoming";
			case "internal":
				return "Internal";
			case "withdrawal":
				return "Withdrawal";
			case "pool_withdrawal":
				return "Pool Withdrawal";
			case "fund_return":
				return "Fund Return";
			default:
				return type;
		}
	};

	const formatEntityType = (entityType: string): string => {
		switch (entityType) {
			case "payment":
				return "Payment";
			case "order":
				return "Order";
			case "transaction":
			case "":
				return "Transaction";
			default:
				return entityType.replace(/_/g, " ");
		}
	};

	const title = transaction
		? `Transaction #${transaction.id}`
		: "Transaction Details";

	return (
		<EntityDrawer
			title={title}
			isLoading={isLoading}
			isNotFound={isNotFound}
			notFoundMessage="Transaction not found"
			parentPath="/transactions"
		>
			{transaction && (
				<div className="p-6 space-y-6">
					<div>
						<h3 className="text-sm font-semibold mb-3">Basic Information</h3>
						<div className="space-y-2 text-sm">
							<DetailRow
								label="Status"
								value={(() => {
									const config = getStatusConfig(transaction.status);
									return (
										<StatusBadge type={config.type} icon={config.icon}>
											{config.label}
										</StatusBadge>
									);
								})()}
							/>
							<DetailRow
								label="Type"
								value={
									<TypeBadge type={getTypeBadgeType(transaction.type)}>
										{formatTransactionType(transaction.type)}
									</TypeBadge>
								}
							/>
							{transaction.entityType.toLowerCase() !==
								transaction.type.toLowerCase() && (
								<DetailRow
									label="Entity Type"
									value={
										<StatusBadge type="info" className="capitalize">
											{formatEntityType(transaction.entityType)}
										</StatusBadge>
									}
								/>
							)}
							<DetailRow
								label="Blockchain"
								value={
									<span className="font-medium capitalize">
										{transaction.blockchain}
									</span>
								}
							/>
							<DetailRow label="Currency" value={transaction.currency} />
							{transaction.entityId && (
								<DetailRow
									label="Entity ID"
									value={
										<span className="font-mono font-medium">
											{transaction.entityId}
										</span>
									}
								/>
							)}
							<DetailRow
								label="Test Mode"
								value={transaction.isTest ? "Yes" : "No"}
							/>
						</div>
					</div>

					<Separator />

					<div>
						<h3 className="text-sm font-semibold mb-3">Amount Details</h3>
						<div className="space-y-2 text-sm">
							<DetailRow
								label="Amount"
								value={
									<span className="font-mono font-medium">
										{formatAmount(transaction.amount)} {transaction.currency}
									</span>
								}
							/>
							<DetailRow
								label="Fact Amount"
								value={
									<span className="font-mono font-medium">
										{formatAmount(transaction.factAmount)}{" "}
										{transaction.currency}
									</span>
								}
							/>
							<DetailRow
								label="Network Fee"
								value={
									<span className="font-mono font-medium">
										{formatAmount(transaction.networkFee)}{" "}
										{transaction.networkFeeCurrency || transaction.currency}
									</span>
								}
							/>
							<DetailRow
								label="Service Fee"
								value={
									<span className="font-mono font-medium">
										{formatAmount(transaction.serviceFee)}{" "}
										{transaction.currency}
									</span>
								}
							/>
							<DetailRow
								label="USD Amount"
								value={
									<span className="font-mono font-medium">
										${formatAmount(transaction.usdAmount)}
									</span>
								}
							/>
						</div>
					</div>

					<Separator />

					<div>
						<h3 className="text-sm font-semibold mb-3">Addresses</h3>
						<div className="space-y-3 text-sm">
							{transaction.senderAddress && (
								<div className="space-y-1">
									<span className="text-muted-foreground">Sender</span>
									<div className="flex items-start justify-between gap-2">
										<span className="font-mono text-xs break-all">
											{transaction.senderAddress}
										</span>
										<CopyButton
											value={transaction.senderAddress}
											label="Address"
										/>
									</div>
								</div>
							)}
							<div className="space-y-1">
								<span className="text-muted-foreground">Recipient</span>
								<div className="flex items-start justify-between gap-2">
									<span className="font-mono text-xs break-all">
										{transaction.recipientAddress}
									</span>
									<CopyButton
										value={transaction.recipientAddress}
										label="Address"
									/>
								</div>
							</div>
							{transaction.transactionHash && (
								<div className="space-y-1">
									<span className="text-muted-foreground">TX Hash</span>
									<div className="flex items-start justify-between gap-2">
										<span className="font-mono text-xs break-all">
											{transaction.transactionHash}
										</span>
										<div className="flex gap-1">
											<CopyButton
												value={transaction.transactionHash}
												label="TX hash"
											/>
											{transaction.explorerLink && (
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8"
													onClick={() => {
														window.open(transaction.explorerLink, "_blank");
													}}
													title="View on blockchain explorer"
												>
													<ExternalLink className="h-4 w-4" />
												</Button>
											)}
										</div>
									</div>
								</div>
							)}
						</div>
					</div>

					<Separator />

					{(transaction.paymentId ||
						transaction.paymentPublicId ||
						transaction.paymentMerchantOrderId ||
						transaction.paymentStatus ||
						transaction.paymentType ||
						transaction.paymentCurrency ||
						transaction.customerEmail) && (
						<>
							<div>
								<div className="flex items-center justify-between mb-3">
									<h3 className="text-sm font-semibold">Payment</h3>
									{transaction.paymentStatus &&
										(() => {
											const config = getStatusConfig(transaction.paymentStatus);
											return (
												<StatusBadge type={config.type} icon={config.icon}>
													{transaction.paymentStatus}
												</StatusBadge>
											);
										})()}
								</div>
								<div className="space-y-2 text-sm">
									{transaction.paymentId && (
										<DetailRow
											label="Payment ID"
											value={
												<span className="font-mono font-medium">
													{transaction.paymentId}
												</span>
											}
										/>
									)}
									{transaction.paymentPublicId && (
										<div className="flex items-start justify-between gap-2 text-sm">
											<div className="flex flex-col gap-1">
												<span className="text-muted-foreground">Public ID</span>
												<span className="font-mono text-xs break-all font-medium">
													{transaction.paymentPublicId}
												</span>
											</div>
											<CopyButton
												value={transaction.paymentPublicId}
												label="Payment public ID"
											/>
										</div>
									)}
									{transaction.paymentMerchantOrderId && (
										<DetailRow
											label="Merchant Order"
											value={transaction.paymentMerchantOrderId}
										/>
									)}
									{transaction.paymentType && (
										<DetailRow
											label="Type"
											value={
												<span className="capitalize font-medium">
													{transaction.paymentType}
												</span>
											}
										/>
									)}
									{transaction.paymentCurrency && (
										<DetailRow
											label="Currency"
											value={transaction.paymentCurrency}
										/>
									)}
									{transaction.customerEmail && (
										<DetailRow
											label="Customer Email"
											value={transaction.customerEmail}
										/>
									)}
								</div>
							</div>

							<Separator />
						</>
					)}

					{(transaction.orderId ||
						transaction.orderUuid ||
						transaction.orderMerchantOrderId ||
						transaction.orderStatus ||
						transaction.orderCryptoCurrency ||
						transaction.orderFiatCurrency) && (
						<>
							<div>
								<div className="flex items-center justify-between mb-3">
									<h3 className="text-sm font-semibold">Order</h3>
									{transaction.orderStatus &&
										(() => {
											const config = getStatusConfig(transaction.orderStatus);
											return (
												<StatusBadge type={config.type} icon={config.icon}>
													{transaction.orderStatus}
												</StatusBadge>
											);
										})()}
								</div>
								<div className="space-y-2 text-sm">
									{transaction.orderId && (
										<DetailRow
											label="Order ID"
											value={
												<span className="font-mono font-medium">
													{transaction.orderId}
												</span>
											}
										/>
									)}
									{transaction.orderUuid && (
										<div className="flex items-start justify-between gap-2 text-sm">
											<div className="flex flex-col gap-1">
												<span className="text-muted-foreground">
													Order UUID
												</span>
												<span className="font-mono text-xs break-all font-medium">
													{transaction.orderUuid}
												</span>
											</div>
											<CopyButton
												value={transaction.orderUuid}
												label="Order UUID"
											/>
										</div>
									)}
									{transaction.orderMerchantOrderId && (
										<DetailRow
											label="Merchant Order"
											value={transaction.orderMerchantOrderId}
										/>
									)}
									{transaction.orderCryptoCurrency && (
										<DetailRow
											label="Crypto Currency"
											value={transaction.orderCryptoCurrency}
										/>
									)}
									{transaction.orderFiatCurrency && (
										<DetailRow
											label="Fiat Currency"
											value={transaction.orderFiatCurrency}
										/>
									)}
								</div>
							</div>

							<Separator />
						</>
					)}

					<div>
						<h3 className="text-sm font-semibold mb-3">Merchant &amp; User</h3>
						<div className="space-y-2 text-sm">
							<DetailRow label="Merchant" value={transaction.merchantName} />
							<DetailRow label="User Name" value={transaction.userName} />
							<DetailRow label="User Email" value={transaction.userEmail} />
						</div>
					</div>

					<Separator />

					<div>
						<h3 className="text-sm font-semibold mb-3">Timestamps</h3>
						<div className="space-y-2 text-sm">
							<DetailRow
								label="Created"
								value={formatDate(transaction.createdAt)}
							/>
							<DetailRow
								label="Updated"
								value={formatDate(transaction.updatedAt)}
							/>
						</div>
					</div>
				</div>
			)}
		</EntityDrawer>
	);
};

export default TransactionDetail;
